<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: SearchResults                                         |
| Section: views/search                                            |
| Purpose: Search results route with filtering and result paging.  |
+------------------------------------------------------------------+
-->
<template>
    <div class="wrapper">
        <!-- Heading -->
        <h1 v-if="searchResultsWithFullName.length > 0">
            {{ $t('searchResults.heading.results', { count: totalResults }) }}
        </h1>
        <h1 v-else-if="!loading && searchResultsWithFullName.length === 0">
            {{ $t('searchResults.heading.noResults') }}
        </h1>
        <h1 v-else-if="loading">
            {{ $t('searchResults.heading.loading') }}
        </h1>

        <!-- Search Box -->
        <div class="search-box" :class="{ focused: isFocused }" ref="searchBox">
            <div class="search-icon">
                <img src="@/assets/icons/magnifier.svg" alt="Search" />
            </div>
            <input type="text" v-model="searchQuery" :placeholder="$t('searchResults.searchPlaceholder')"
                class="search-input" @focus="onFocus" @blur="onBlur" @keyup.enter="submitSearch" />
            <button type="button" :class="{
                'submit-enabled': searchQuery,
                'submit-disabled': !searchQuery
            }" @click="submitSearch">
                <img src="@/assets/icons/search_submit.svg" alt="Search" />
            </button>
            <!-- Reset Button: appears only when there's input -->
            <button type="button" class="reset-button" v-if="searchQuery" @click="resetSearch">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="16" viewBox="0 0 12 16" fill="none">
                    <path
                        d="M10.7062 4.70627C11.0968 4.31565 11.0968 3.68127 10.7062 3.29065C10.3155 2.90002 9.68115 2.90002 9.29053 3.29065L5.9999 6.5844L2.70615 3.29377C2.31553 2.90315 1.68115 2.90315 1.29053 3.29377C0.899902 3.6844 0.899902 4.31877 1.29053 4.7094L4.58428 8.00002L1.29365 11.2938C0.903027 11.6844 0.903027 12.3188 1.29365 12.7094C1.68428 13.1 2.31865 13.1 2.70928 12.7094L5.9999 9.41565L9.29365 12.7063C9.68428 13.0969 10.3187 13.0969 10.7093 12.7063C11.0999 12.3156 11.0999 11.6813 10.7093 11.2906L7.41553 8.00002L10.7062 4.70627Z"
                        fill="#1D1D1D" />
                </svg>
            </button>
        </div>

        <!-- Search Results -->
        <div v-if="searchResultsWithFullName.length > 0" class="SearchResults_wrapper">
            <table>
                <thead>
                    <tr>
                        <th>#</th>
                        <th @click="sortResults('lastname')">
                            {{ $t('searchResults.table.name') }}
                            <img :src="getSortIcon('lastname')" class="sort-icon" />
                        </th>
                        <th @click="sortResults('birthdatetr')">
                            {{ $t('searchResults.table.birthDate') }}
                            <img :src="getSortIcon('birthdatetr')" class="sort-icon" />
                        </th>
                        <th @click="sortResults('birthplace')">
                            {{ $t('searchResults.table.birthPlace') }}
                            <img :src="getSortIcon('birthplace')" class="sort-icon" />
                        </th>
                        <th class="search-actions-header">
                            <span class="sr-only">{{ $t('searchResults.actionsAlt') }}</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(result, index) in searchResultsWithFullName" :key="result.id"
                        :class="{ 'disabled-row': isPrivateResult(result) }"
                        @click="handleRowClick(result, $event)" @auxclick="handleRowAuxClick(result, $event)"
                        @mouseenter="ensureBranchStatus(result)" @keydown.enter.prevent="handleRowClick(result, $event)"
                        @keydown.space.prevent="handleRowClick(result, $event)" @focusin="ensureBranchStatus(result)"
                        tabindex="0" role="link"
                        :aria-label="$t('searchResults.ariaRowLabel', { name: result.fullName || result.firstname || result.personID || '' })">
                        <td>{{ (currentPage - 1) * resultsPerPage + index + 1 }}</td>
                        <td>
                            <span v-if="isPrivateResult(result)" class="locked_name">
                                <img src="@/assets/icons/lock_black.svg" /> {{ $t('searchResults.privacyHidden') }}
                            </span>
                            <span v-else-if="result.lastname || result.firstname">
                                {{ result.lastname ? (result.lastname + ', ' + result.firstname) : result.firstname }}
                            </span>
                            <span v-else>{{ $t('searchResults.nameUnknown') }}</span>
                        </td>
                        <td>
                            <p v-if="isPrivateResult(result)"></p>
                            <p v-else-if="result.birthdatetr && result.birthdatetr !== '0000-00-00'">
                                {{ formatShortDate(result.birthdatetr) }}
                            </p>
                            <p v-else-if="result.altbirthdatetr && result.altbirthdatetr !== '0000-00-00'">
                                {{ formatShortDate(result.altbirthdatetr) }}{{ $t('searchResults.baptizedSuffix') }}
                            </p>
                            <p v-else>-</p>
                        </td>
                        <td>
                            <p v-if="isPrivateResult(result)"></p>
                            <p v-else-if="result.birthplace">
                                {{ result.birthplace }}
                            </p>
                            <p v-else-if="result.altbirthplace">
                                {{ result.altbirthplace }}{{ $t('searchResults.baptismPlaceSuffix') }}
                            </p>
                            <p v-else>-</p>
                        </td>
                        <td class="search-actions-cell">
                            <div class="search-row-dropdown" @click.stop
                                :class="{ 'search-row-dropdown--open': openActionMenuPersonID === result.personID }">
                                <button type="button" class="search-row-dropdown-toggle"
                                    @click.stop="toggleRowDropdown(result.personID)">
                                    <img src="@/assets/icons/dropdown_white.svg" :alt="$t('searchResults.actionsAlt')">
                                </button>
                                <div class="search-row-dropdown-menu">
                                    <button type="button" @click.stop="goToPersonPage(result.personID, $event)">
                                        {{ $t('searchResults.actions.viewPerson') }}
                                    </button>
                                    <button v-if="shouldShowKinshipAction(result.personID)" type="button"
                                        @click.stop="goToKinshipCalculator(result, $event)">
                                        {{ $t('searchResults.actions.calculateRelationship') }}
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <ControlWrapper :currentPage="currentPage" :totalPages="totalPages" :totalResults="totalResults"
                :resultsPerPageOptions="resultsPerPageOptions" v-model:selectedResultsPerPage="resultsPerPage"
                @changeResultsPerPage="handleChangeResultsPerPage" @goToPage="handleGoToPage" @prevPage="handlePrevPage"
                @nextPage="handleNextPage" />
        </div>

        <!-- No Results Message -->
        <p v-else-if="!loading && searchResultsWithFullName.length === 0" class="no_results"
            v-html="randomNoResultsMessage"></p>

        <!-- Loader -->
        <LoadingSpinner v-else-if="loading" />
    </div>
</template>

<script>
import { formatShortDate } from '@/utils/dateUtils';
import authenticatedApiClient from '@/api/authenticatedApiClient';
import { useAuth } from '@/composables/useAuth';
import { useTableSort } from '@/composables/useTableSort';
import { usePagination } from '@/composables/usePagination';
import { useRoute } from 'vue-router';
import { getPersonBranches } from '@/api/personService';
import ControlWrapper from '../../components/ControlWrapper.vue';
import { applyGenericSeo } from '@/utils/seoUtils';
import LoadingSpinner from '@/atoms/LoadingSpinner.vue';
import { useLocale } from '@/composables/useLocale';
import { isPrivateLivingPerson } from '@/utils/personPrivacy';

export default {
    components: { ControlWrapper, LoadingSpinner },
    setup() {
        const { authToken, username, logout } = useAuth();
        const route = useRoute();
        const { localePath } = useLocale();

        // Initialize sorting from route query params (or use defaults)
        const initialField = route.query.sortField || null;
        const initialOrder = route.query.sortOrder || 'asc';
        const { sortField, sortOrder, getSortIcon, sortBy } = useTableSort(
            null,
            initialField,
            initialOrder
        );

        // Initialize pagination from route query params
        const initialPage = parseInt(route.query.page) || 1;
        const initialPerPage = parseInt(route.query.limit) || 10;
        const {
            currentPage,
            resultsPerPage,
            totalResults,
            totalPages,
            goToPage,
            nextPage,
            prevPage,
            changeResultsPerPage,
            setTotalResults
        } = usePagination({ initialPage, initialPerPage });

        return {
            authToken,
            username,
            logout,
            sortField,
            sortOrder,
            getSortIcon,
            sortBy,
            currentPage,
            resultsPerPage,
            totalResults,
            totalPages,
            goToPage,
            nextPage,
            prevPage,
            changeResultsPerPage,
            setTotalResults,
            localePath
        };
    },
    data() {
        return {
            searchResults: [],
            loading: false,
            resultsPerPageOptions: [10, 20, 50, 100],
            searchQuery: this.$route.query.q || '',
            isFocused: false,
            randomNoResultsMessage: '',
            searchDebounceTimer: null,
            isSyncingQueryFromRoute: false,
            lastRequestId: 0,
            branchStatusByPerson: {},
            openActionMenuPersonID: null
        };
    },
    computed: {
        searchResultsWithFullName() {
            return this.searchResults.map(result => {
                let fullName = result.firstname;
                if (result.lastname) {
                    fullName = result.lastname + ', ' + result.firstname;
                }
                return { ...result, fullName };
            });
        }
    },
    watch: {
        searchQuery(newQuery, oldQuery) {
            if (newQuery === oldQuery || this.isSyncingQueryFromRoute) {
                return;
            }

            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                const trimmedQuery = (newQuery || '').trim();
                const routeQuery = (this.$route.query.q || '').trim();

                if (trimmedQuery === routeQuery) {
                    return;
                }

                // Query backend only for empty or 2+ chars to keep instant search responsive.
                if (trimmedQuery.length === 0 || trimmedQuery.length >= 2) {
                    this.currentPage = 1;
                    this.resultsPerPage = 10;
                    this.updateRandomNoResultsMessage();
                    this.updateRoute(
                        {
                            q: trimmedQuery || undefined,
                            page: 1,
                            limit: 10
                        },
                        true
                    );
                }
            }, 300);
        },
        '$route.query': {
            handler(newQuery) {
                const nextQuery = (newQuery.q || '').trim();
                if (nextQuery !== this.searchQuery) {
                    this.isSyncingQueryFromRoute = true;
                    this.searchQuery = nextQuery;
                    this.isSyncingQueryFromRoute = false;
                }

                this.currentPage = parseInt(newQuery.page) || 1;
                this.resultsPerPage = parseInt(newQuery.limit) || 10;
                this.sortField = newQuery.sortField || null;
                this.sortOrder = newQuery.sortOrder === 'desc' ? 'desc' : 'asc';

                this.fetchSearchResults();
            },
            deep: true
        }
    },
    methods: {
        formatShortDate,
        sortResults(field) {
            this.sortBy(field);
            this.updateRoute();
        },
        submitSearch() {
            clearTimeout(this.searchDebounceTimer);
            const trimmedQuery = (this.searchQuery || '').trim();

            if (trimmedQuery.length > 0 && trimmedQuery.length < 2) {
                return;
            }

            this.currentPage = 1;
            this.resultsPerPage = 10;
            this.updateRandomNoResultsMessage();
            this.updateRoute({
                q: trimmedQuery || undefined,
                page: 1,
                limit: 10
            });
        },
        resetSearch() {
            clearTimeout(this.searchDebounceTimer);
            this.searchQuery = '';
            this.currentPage = 1;
            this.resultsPerPage = 10;
            this.updateRandomNoResultsMessage();
            this.updateRoute({
                q: undefined,
                page: 1,
                limit: 10
            });
        },
        onFocus() {
            this.isFocused = true;
        },
        onBlur() {
            this.isFocused = false;
        },
        fetchSearchResults() {
            const requestId = ++this.lastRequestId;
            this.loading = true;
            const searchQuery = this.$route.query.q || '';
            const page = parseInt(this.$route.query.page) || 1;
            const limit = parseInt(this.$route.query.limit) || 10;
            const params = {
                q: searchQuery || undefined,
                page,
                limit
            };
            if (this.sortField) {
                params.sortField = this.sortField;
                params.sortOrder = this.sortOrder;
            }
            const requestConfig = { params };
            authenticatedApiClient
                .get('/search', requestConfig)
                .then(response => {
                    if (requestId !== this.lastRequestId) {
                        return;
                    }
                    this.searchResults = response.data.results;
                    this.setTotalResults(response.data.total);
                })
                .catch(error => {
                    if (requestId !== this.lastRequestId) {
                        return;
                    }
                    console.error('Error during search:', error);
                })
                .finally(() => {
                    if (requestId === this.lastRequestId) {
                        this.loading = false;
                    }
                });
        },
        updateRandomNoResultsMessage() {
            const messages = this.$tm('searchResults.noResultsMessages');
            if (!messages?.length) return;
            const randomIndex = Math.floor(Math.random() * messages.length);
            const raw = messages[randomIndex];
            this.randomNoResultsMessage = typeof raw === 'function'
                ? raw({ query: this.searchQuery })
                : String(raw).replace(/\{query\}/g, this.searchQuery);
        },

        isPrivateResult(result) {
            return isPrivateLivingPerson(result, this.authToken);
        },
        handleRowClick(result, event) {
            if (this.isPrivateResult(result)) {
                this.goToLogin(result.personID);
                return;
            }

            this.goToPersonPage(result.personID, event);
        },
        handleRowAuxClick(result, event) {
            if (event.button === 1) {
                this.handleRowClick(result, event);
            }
        },
        openPath(path, event) {
            const shouldOpenInNewTab =
                event?.metaKey ||
                event?.ctrlKey ||
                event?.button === 1;

            if (shouldOpenInNewTab) {
                window.open(path, '_blank', 'noopener');
                return;
            }

            this.$router.push(path);
        },
        goToPersonPage(personID, event) {
            this.openPath(this.localePath(`/person/${personID}`), event);
        },
        goToKinshipCalculator(result, event) {
            const path = this.$router.resolve({
                name: 'RelationshipTree',
                params: {
                    personID1: 'I43',
                    personID2: result.personID,
                },
                query: {
                    source: 'person',
                    personId: result.personID,
                },
            }).href;

            this.openPath(path, event);
        },
        goToLogin(personID) {
            this.$router.push({
                path: this.localePath('/login'),
                query: {
                    redirect: personID ? this.localePath(`/person/${personID}`) : this.$route.fullPath,
                },
            });
        },
        async ensureBranchStatus(result) {
            const personID = result?.personID;
            if (!personID || this.branchStatusByPerson[personID]) {
                return;
            }

            this.branchStatusByPerson = {
                ...this.branchStatusByPerson,
                [personID]: {
                    loading: true,
                    hasBranches: null,
                    loadError: false
                }
            };

            try {
                const branches = await getPersonBranches(personID);
                if (!Array.isArray(branches)) {
                    throw new TypeError('Invalid branches response');
                }
                const hasBranches = branches.length > 0;

                this.branchStatusByPerson = {
                    ...this.branchStatusByPerson,
                    [personID]: {
                        loading: false,
                        hasBranches,
                        loadError: false
                    }
                };
            } catch (error) {
                console.error(`Error fetching branches for person ${personID}:`, error);
                this.branchStatusByPerson = {
                    ...this.branchStatusByPerson,
                    [personID]: {
                        loading: false,
                        hasBranches: null,
                        loadError: true
                    }
                };
            }
        },
        shouldShowKinshipAction(personID) {
            const status = this.branchStatusByPerson[personID];
            if (!status || status.loading || status.loadError) {
                return false;
            }

            return status.hasBranches === false;
        },
        toggleRowDropdown(personID) {
            this.openActionMenuPersonID = this.openActionMenuPersonID === personID ? null : personID;
        },
        closeRowDropdown() {
            this.openActionMenuPersonID = null;
        },
        onDocumentClick(event) {
            const clickedInsideDropdown = event.target?.closest?.('.search-row-dropdown');
            if (!clickedInsideDropdown) {
                this.closeRowDropdown();
            }
        },
        // Pagination wrapper methods
        handleNextPage() {
            this.nextPage();
            this.updateRoute();
        },
        handlePrevPage() {
            this.prevPage();
            this.updateRoute();
        },
        handleGoToPage(page) {
            this.goToPage(page);
            this.updateRoute();
        },
        handleChangeResultsPerPage(newLimit) {
            this.changeResultsPerPage(newLimit);
            this.updateRoute();
            this.fetchSearchResults();
        },
        updateRoute(overrides = {}, replace = false) {
            const query = {
                q: overrides.q !== undefined ? overrides.q : this.$route.query.q,
                page: overrides.page !== undefined ? overrides.page : this.currentPage,
                limit: overrides.limit !== undefined ? overrides.limit : this.resultsPerPage,
                sortField: overrides.sortField !== undefined ? overrides.sortField : this.sortField,
                sortOrder: overrides.sortOrder !== undefined ? overrides.sortOrder : this.sortOrder
            };

            const cleanedQuery = Object.fromEntries(
                Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== '')
            );

            const routerMethod = replace ? 'replace' : 'push';
            this.$router[routerMethod]({
                name: 'SearchResults',
                query: cleanedQuery
            });
        }
    },
    beforeUnmount() {
        clearTimeout(this.searchDebounceTimer);
        document.removeEventListener('click', this.onDocumentClick);
    },
    mounted() {
        const title = this.$t('searchResults.seoTitle');
        const description = this.searchQuery
            ? this.$t('searchResults.seoDescriptionWithQuery', { query: this.searchQuery })
            : this.$t('searchResults.seoDescription');
        const image = "https://sejarah-thumann.online/img/sejarah_thumann1080x781.4fc54a6a.jpg"; // Standaard banner-afbeelding
        const url = window.location.href;

        applyGenericSeo({ title, description, image, url }, { includeTwitter: true });

        if (!this.searchQuery) {
            this.searchQuery = this.$route.query.q || '';
        }
        this.updateRandomNoResultsMessage();
        this.fetchSearchResults();
        document.addEventListener('click', this.onDocumentClick);
    }
};
</script>

<style scoped>
h1 {
    text-align: center;
}

.wrapper {
    padding: var(--page-padding);
    min-height: calc(100vh - 106px - 516px);
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.SearchResults_wrapper {
    background: var(--brand-bg-1);
    box-shadow: var(--shadow-card-lg);
    padding: 48px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    /* gap: 48px; */
}

table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
}

th {
    font-weight: 600;
}

thead th {
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
}

th:not(:first-of-type) {
    cursor: pointer;
}

tr {
    display: flex;
    padding: 12px 0;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;
    border-bottom: 1px solid #EEE;
}

tr:not(thead tr) {
    cursor: pointer;
}

tr:not(thead tr):focus-visible {
    outline: 2px solid var(--brand-color-1);
    outline-offset: -2px;
}

th:nth-of-type(1),
td:nth-of-type(1) {
    flex: 0 0 3%;
    text-align: left;
}

th:nth-of-type(2),
td:nth-of-type(2) {
    flex: 0 0 25%;
    text-align: left;
}

th:nth-of-type(3),
td:nth-of-type(3) {
    flex: 0 0 12%;
    text-align: right;
}

thead th:nth-of-type(3) {
    justify-content: flex-end;
}

th:nth-of-type(4),
td:nth-of-type(4) {
    flex: 1 1 auto;
    text-align: left;
}

th:nth-of-type(5),
td:nth-of-type(5) {
    flex: 0 0 28px;
    text-align: right;
}

.search-actions-header {
    justify-content: flex-end;
    cursor: default !important;
}

.search-actions-cell {
    display: flex;
    justify-content: flex-end;
}

.search-row-dropdown {
    position: relative;
    display: inline-block;
}

.search-row-dropdown-toggle {
    display: none;
    background: var(--brand-color-1);
    border: none;
    border-radius: 4px;
    padding: 6px 9px;
    cursor: pointer;
    transition: background-color .2s ease;
}

tr:hover .search-row-dropdown-toggle,
tr:focus-within .search-row-dropdown-toggle {
    display: inline-block;
}

.search-row-dropdown-toggle:hover {
    background: var(--brand-color-2);
}

.search-row-dropdown-menu {
    display: none;
    position: absolute;
    top: 100%;
    right: -12px;
    background: var(--brand-bg-1);
    box-shadow: var(--shadow-card-lg);
    border-radius: 16px;
    z-index: 1000;
    padding: 8px 0;
    overflow: hidden;
    min-width: 230px;
}

.search-row-dropdown:hover .search-row-dropdown-menu,
.search-row-dropdown--open .search-row-dropdown-menu {
    display: block;
}

.search-row-dropdown-menu button {
    width: 100%;
    padding: 8px 24px;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    transition: background-color .2s;
}

.search-row-dropdown-menu button:hover {
    background-color: #f5f5f5;
    text-decoration: underline;
}

.sr-only {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    border: 0 !important;
}

tr:not(thead tr):hover {
    background-color: #f0f0f0;
    text-decoration: underline;
}

.search-box {
    margin: 0 auto 48px;
}

/* .search-box {
    margin: 0 auto 48px;
    position: relative;
    width: 100%;
    max-width: 400px;
}

.search-box .search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
}

.search-input {
    width: 100%;
    padding: 10px 40px 10px 40px;
    border: 1px solid #ddd;
    border-radius: var(--border-radius-md);
} */

@media (max-width: 860px) {
    .wrapper {
        padding: var(--page-padding-mobile);
    }

    .SearchResults_wrapper {
        padding: 24px;
    }

    .search-box {
        margin-bottom: 24px;
    }

    thead tr {
        padding-top: 0;
    }

    th {
        justify-content: space-between !important;
    }

    tr {
        flex-direction: column;
        gap: 0;
    }

    th:nth-of-type(1),
    td:nth-of-type(1) {
        width: 100%;
        display: none;
    }

    th:nth-of-type(2),
    td:nth-of-type(2) {
        width: 100%;
    }

    th:nth-of-type(3),
    td:nth-of-type(3) {
        width: 100%;
        text-align: left;
    }

    thead th:nth-of-type(3) {
        justify-content: flex-start;
    }

    th:nth-of-type(4),
    td:nth-of-type(4) {
        width: 100%;
    }

    th:nth-of-type(5),
    td:nth-of-type(5) {
        width: 100%;
        text-align: left;
    }

    .search-actions-header {
        justify-content: flex-start;
    }

    .search-row-dropdown-toggle {
        display: inline-block;
        margin-top: 8px;
    }
}

@media (max-width: 600px) {
    .search-box {
        width: 100%;
    }
}
</style>
